<script lang="ts">
  import { createContext } from 'reka-ui'

  export type ModalTypes = 'dialog' | 'drawer' | 'none'

  export type ModalState = Ref<unknown> | undefined

  export type ModalCheckerContext = {
    modalType: ModalTypes
    isOpen: Ref<boolean | undefined>
  }

  const [_useModalContext, provideModalChecker] =
    createContext<ModalCheckerContext>('gs-modal-checker')

  /** Used to check the type of the modal. Useful in for determining subcomponent's styles. */
  export function useModalContext(): ModalCheckerContext {
    try {
      return _useModalContext()
    } catch (error) {
      return {
        modalType: 'none',
        isOpen: ref(false)
      }
    }
  }
</script>

<script setup lang="ts">
  import { useUser } from '@gat-ship/lib'
  import { globalUtils } from '@gat-ship/utils'
  import type { IconValues } from '@gat-ship/icons'
  import { GatDialog, type GatDialogProps } from '../gat-dialog'
  import {
    computed,
    h,
    nextTick,
    onMounted,
    reactive,
    ref,
    type Component,
    type Ref,
    type HTMLAttributes
  } from 'vue'
  import { AppDrawer } from '../app/drawer'
  import type { UnsavedChangesOptions } from '@gat-ship/lib/router'
  import { GatButton, type GatButtonProps } from '../gat-button'

  export interface ModalProps {
    class?: HTMLAttributes['class']
    type?: ModalTypes
    title: string
    icon?: IconValues
    buttons?: GatDialogProps['buttons']
    closeOnOutsideClick?: boolean
    noFooter?: boolean
    description?: string | ((...args: any[]) => string)
    maxWidth?: number
    loading?: boolean
    hideCloseButton?: boolean
  }

  defineOptions({ name: 'GatModal' })

  const props = defineProps<ModalProps>()

  const hasEvent = reactive({ clickCancel: false, clickSave: false })

  onMounted(() => {
    hasEvent.clickCancel =
      globalUtils.functions.components.hasEmitListener('click:cancel')
    hasEvent.clickSave =
      globalUtils.functions.components.hasEmitListener('click:save')
  })

  const modal = ref()

  const isOpen = defineModel<boolean>('open', { required: false })

  function setModalState(values: ModalState) {
    if (modal.value) {
      if (props.type === 'dialog' && 'setDialogDataState' in modal.value) {
        modal.value?.setDialogDataState(values)
      } else if ('setDrawerDataState' in modal.value) {
        modal.value?.setDrawerDataState(values)
      }
    }
  }

  function setUnsavedChangesOptions(options: Partial<UnsavedChangesOptions>) {
    if (modal.value && 'setUnsavedChangesOptions' in modal.value) {
      modal.value?.setUnsavedChangesOptions(options)
    }
  }

  const { userPreferences } = useUser()

  provideModalChecker({
    modalType: props.type ?? userPreferences.getUserPreferences().modalType,
    isOpen
  })

  const component = computed((): Component => {
    const type = props.type ?? userPreferences.getUserPreferences().modalType
    if (type === 'dialog') {
      return h(GatDialog, props)
    }
    return h(AppDrawer, props)
  })

  function showModal(inputState?: ModalState) {
    if (inputState) {
      setModalState(inputState)
    }
    isOpen.value = true
  }

  function clearErrorMessage() {
    if (modal.value && 'clearErrorMessage' in modal.value) {
      modal.value?.clearErrorMessage()
    }
  }

  /** Will close the modal.
   * @param options.resetState - Whether to reset the state of the modal. @default true
   */
  function closeModal(options?: {
    resetState?: boolean
    preventTabClose?: false
  }) {
    if (options?.resetState ?? true) {
      setModalState(undefined)
    }
    if (options?.preventTabClose !== undefined) {
      setUnsavedChangesOptions({ preventTabClose: options.preventTabClose })
    }
    isOpen.value = false
    nextTick(() => {
      setUnsavedChangesOptions({ preventTabClose: true })
      clearErrorMessage()
    })
  }

  const emit = defineEmits<{
    (e: 'click:save'): void
    (e: 'click:cancel'): void
  }>()

  async function onSaveClicked() {
    emit('click:save')
  }

  async function onCancelClick() {
    if (hasEvent.clickCancel) {
      emit('click:cancel')
    }
    closeModal()
  }

  const okButtonProps = computed((): GatButtonProps | undefined => {
    const defaults = {
      label: 'Save Changes',
      text: 'Save Changes'
    }
    if (props.buttons?.ok === false) {
      return undefined
    }
    if (props.buttons?.ok) {
      return {
        ...defaults,
        ...props.buttons.ok
      }
    }
    if (hasEvent.clickSave) {
      return {
        ...defaults,
        ...props.buttons?.ok
      }
    }
    return undefined
  })
  const cancelButtonProps = computed((): GatButtonProps | undefined => {
    if (props.buttons?.cancel === false) {
      return undefined
    }
    if (props.buttons?.cancel !== undefined) {
      return props.buttons.cancel
    }
    return {
      label: 'Cancel',
      variant: 'ghost'
    }
  })

  defineExpose({
    setModalState,
    setUnsavedChangesOptions,
    showModal,
    closeModal,
    setErrorMessage: (message: string) => {
      if (modal.value && 'setErrorMessage' in modal.value) {
        modal.value?.setErrorMessage(message)
      }
    },
    clearErrorMessage
  })
</script>
<template>
  <div v-if="props.type === 'none'" class="flex flex-col gap-4">
    <div
      :data-state="isOpen ? 'open' : 'closed'"
      class="block data-[state=open]:hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      @click="isOpen = true"
    >
      <slot name="trigger" />
    </div>
    <div
      :data-state="isOpen ? 'open' : 'closed'"
      class="hidden data-[state=open]:flex data-[state=open]:flex-col data-[state=open]:gap-4 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
    >
      <slot />
      <div class="flex grow justify-end gap-2">
        <gat-button
          v-if="cancelButtonProps"
          v-bind="cancelButtonProps"
          class="text-soft w-fit"
          variant="outline"
          size="sm"
          @click="onCancelClick()"
        />
        <gat-button
          v-if="okButtonProps"
          v-bind="okButtonProps"
          class="w-fit"
          size="sm"
          @click="[emit('click:save'), okButtonProps.onClick]"
        />
      </div>
    </div>
  </div>
  <component
    v-else
    :is="component"
    :max-width="props.maxWidth"
    :no-footer="props.noFooter"
    ref="modal"
    v-model:open="isOpen"
    :class="props.class"
    :hide-close-button="props.hideCloseButton"
    @click:save="onSaveClicked"
    @click:cancel="onCancelClick()"
  >
    <template #header>
      <slot name="header" />
    </template>
    <template #actions>
      <slot name="actions" />
    </template>
    <template #title>
      <slot name="title" />
    </template>
    <template #trigger>
      <slot name="trigger" />
    </template>
    <div class="flex flex-col gap-3 pb-5">
      <slot />
    </div>
    <template #footer>
      <slot name="footer" />
    </template>
  </component>
</template>
